import { useState } from "react";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { ROLES } from "@/constants/testIds";
import RoleDialog from "@/components/team/RoleDialog";

// Fix AW (2026-10-27): roles personalizados. La administradora crea un rol con
// las secciones que puede ver (Inventario, Pagos, Reportes) y despues se lo
// asigna a cada persona al agregarla en el Equipo.
export default function RolesCard({ areas, roles, onChanged }) {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const areaLabel = (key) => areas.find((a) => a.key === key)?.label || key;

  const handleSubmit = async (payload, existing) => {
    try {
      if (existing) {
        await api.put(`/roles/${existing.id}`, payload);
        toast({ title: "Rol actualizado", description: `${payload.name} fue actualizado.` });
      } else {
        await api.post("/roles", payload);
        toast({ title: "Rol creado", description: `Ya puedes asignar "${payload.name}" a una persona del equipo.` });
      }
      setDialogOpen(false);
      setEditing(null);
      onChanged();
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/roles/${deleteTarget.id}`);
      toast({ title: "Rol eliminado", description: `${deleteTarget.name} fue eliminado.` });
      setDeleteTarget(null);
      onChanged();
    } catch (err) {
      toast({ title: "No se pudo eliminar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
      setDeleteTarget(null);
    }
  };

  return (
    <div className="bg-white border border-border rounded-xl shadow-sm p-5 space-y-4" data-testid={ROLES.card}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-xl text-foreground">Roles personalizados</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Para quien solo necesita ver algunas secciones (por ejemplo, contabilidad). Crea el rol y luego asígnalo al agregar a la persona.
          </p>
        </div>
        <Button variant="outline" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid={ROLES.addButton}>
          <Plus className="w-4 h-4" /> Nuevo rol
        </Button>
      </div>

      {roles.length === 0 ? (
        <div className="py-6 text-center" data-testid={ROLES.emptyState}>
          <ShieldCheck className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Todavía no creaste ningún rol personalizado.</p>
        </div>
      ) : (
        <div className="divide-y divide-border border border-border rounded-lg">
          {roles.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 flex-wrap p-3" data-testid={`${ROLES.rowPrefix}${r.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{r.name}</p>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {r.areas.map((k) => (
                    <Badge key={k} className="bg-[#EDE7F3] text-[#6B4E8A] border-none font-normal">{areaLabel(k)}</Badge>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  {r.members === 0 ? "Sin personas asignadas" : r.members === 1 ? "1 persona" : `${r.members} personas`}
                </p>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => { setEditing(r); setDialogOpen(true); }} data-testid={`${ROLES.editButtonPrefix}${r.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(r)} data-testid={`${ROLES.deleteButtonPrefix}${r.id}`}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <RoleDialog open={dialogOpen} onOpenChange={setDialogOpen} role={editing} areas={areas} onSubmit={handleSubmit} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar el rol {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Si hay personas con este rol, primero cámbiales el rol: el sistema no deja eliminarlo mientras alguien lo use.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={ROLES.deleteCancelButton}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90" data-testid={ROLES.deleteConfirmButton}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
